import React, { useState, useRef, useEffect } from 'react';
import { HiMenu } from 'react-icons/hi';
import { AiFillCloseCircle } from 'react-icons/ai';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { Link, Route, Routes } from 'react-router-dom';

import { Sidebar, UserProfile } from '../components';
import { userQuery } from '../utils/data';
import { client, urlFor } from '../client';
import Pins from './Pins';
import logo from '../assets/logo.png';
import logoWhite from '../assets/logowhite.png';
import { fetchUser } from '../utils/fetchUser';
import { useTheme } from '../context/ThemeContext';
import Footer from '../components/Footer';
import PrivacyPolicy from '../components/PrivacyPolicy';
import Terms from '../components/Terms';
import About from '../components/About';
import AdminPanel from '../components/admin/AdminPanel';
import AdminRoute from '../components/admin/AdminRoute';

const Home = () => {
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const [user, setUser] = useState(null);
  const [showIntro, setShowIntro] = useState(() => {
    const saved = localStorage.getItem('showIntro');
    return saved !== null ? JSON.parse(saved) : window.innerWidth >= 768;
  });
  const [lastScrollY, setLastScrollY] = useState(0);
  const scrollRef = useRef(null);
  const { theme } = useTheme();
  const userInfo = fetchUser();

  useEffect(() => {
    localStorage.setItem('showIntro', JSON.stringify(showIntro));
  }, [showIntro]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && showIntro) {
        setShowIntro(false);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, showIntro]);

  useEffect(() => {
    if (userInfo) {
      const query = userQuery(userInfo._id);
      client
        .fetch(query)
        .then((data) => {
          if (data && data.length > 0) {
            setUser(data[0]);
          }
        })
        .catch(console.error);
    }
  }, [userInfo]);

  useEffect(() => {
    scrollRef.current?.scrollTo(0, 0);
  }, []);

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-height duration-300 ease-out">
      <div className="flex flex-1 md:flex-row flex-col overflow-hidden">
        {/* Sidebar Desktop */}
        <div className="hidden md:flex h-screen flex-initial border-r border-gray-200 dark:border-gray-700">
          <Sidebar user={user} />
        </div>

        {/* Mobile Header */}
        <div className="flex md:hidden flex-row sticky top-0 z-20 bg-white dark:bg-gray-900 shadow-sm">
          <div className="p-3 w-full flex justify-between items-center">
            <HiMenu
              fontSize={36}
              className="cursor-pointer text-gray-800 dark:text-gray-100 transition-colors hover:text-blue-600 dark:hover:text-blue-400"
              onClick={() => setToggleSidebar(true)}
              aria-label="Open sidebar menu"
            />
            <Link to="/" className="flex items-center">
              <img
                src={theme === 'dark' ? logoWhite : logo}
                alt="Imagera Logo"
                className="w-28 object-contain"
              />
            </Link>
            {user?._id && (
              <Link to={`user-profile/${user._id}`} aria-label="User Profile">
                <img
                  src={
                    user.image && typeof user.image === 'string' && user.image.trim() !== ''
                      ? user.image
                      : user.image
                        ? urlFor(user.image).url()
                        : '/default-avatar.png'
                  }
                  alt="Profile"
                  className="w-10 h-10 rounded-full object-cover border-2 border-blue-500"
                />
              </Link>
            )}
          </div>

          {/* Sidebar Mobile */}
          {toggleSidebar && (
            <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-30 flex">
              <div className="w-4/5 max-w-xs bg-white dark:bg-gray-900 h-full shadow-lg animate-slide-in relative">
                <div className="absolute top-3 right-3">
                  <AiFillCloseCircle
                    fontSize={30}
                    className="cursor-pointer text-gray-800 dark:text-gray-100 hover:text-red-600 dark:hover:text-red-400"
                    onClick={() => setToggleSidebar(false)}
                    aria-label="Close sidebar menu"
                  />
                </div>
                <Sidebar user={user} closeToggle={setToggleSidebar} />
              </div>
              <div
                className="flex-grow"
                onClick={() => setToggleSidebar(false)}
                aria-hidden="true"
              />
            </div>
          )}
        </div>

        {/* Main content */}
        <main
          className="pb-10 flex-1 overflow-y-scroll bg-gray-50 dark:bg-gray-900 hide-scrollbar"
          ref={scrollRef}
        >
          <div className="px-4 md:px-10 mx-auto">
            <button
              onClick={() => setShowIntro(!showIntro)}
              className="group flex items-center justify-center gap-2 w-full px-5 py-2 mt-6 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white font-semibold shadow-lg hover:shadow-xl transition-transform duration-300 focus:outline-none focus:ring-2 focus:ring-purple-400"
              aria-expanded={showIntro}
              aria-controls="intro-section"
            >
              <span
                className={`transform transition-transform duration-300 ${showIntro ? 'rotate-180' : ''
                  }`}
              >
                {showIntro ? <FaChevronUp /> : <FaChevronDown />}
              </span>
              {showIntro ? 'Hide Introduction' : 'Show Introduction'}
            </button>

            <section
              id="intro-section"
              className={`overflow-hidden transform transition-all duration-700 ease-in-out
                ${showIntro
                  ? 'max-h-[1000px] opacity-100 scale-y-100 mt-6'
                  : 'max-h-0 opacity-0 scale-y-95 mt-0'
                }`}
              aria-hidden={!showIntro}
            >
              <div
                className="max-w-4xl mx-auto p-8 bg-white dark:bg-slate-800 rounded-3xl shadow-lg
                text-center transition-colors duration-500"
                style={{
                  backgroundImage:
                    theme === 'dark'
                      ? 'radial-gradient(circle at center, #2a2f45 0%, #1a202c 100%)'
                      : 'radial-gradient(circle at center, #e0f2fe 0%, #bae6fd 100%)',
                }}
              >
                <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white mb-6">
                  Welcome to Imagera
                </h1>
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  Imagera is a vibrant community where artists bring imagination to life.
                  Share your original artwork, discover unique pieces, and connect with creative minds.
                </p>

                <ul className="space-y-5 text-left max-w-3xl mx-auto">
                  {[
                    {
                      icon: '🔍',
                      color: 'text-blue-600 dark:text-blue-400',
                      title: 'Explore',
                      desc: 'Discover artwork from talented creators on the home feed or use the search bar to find specific styles or artists.',
                    },
                    {
                      icon: '💬',
                      color: 'text-green-600 dark:text-green-400',
                      title: 'Interact',
                      desc: 'Click on any post to view details, like it to show support, or comment to start meaningful conversations.',
                    },
                    {
                      icon: '➕',
                      color: 'text-purple-600 dark:text-purple-400',
                      title: 'Create',
                      desc: 'Upload your artwork, add descriptions, tags, and let the community see your creativity shine.',
                    },
                    {
                      icon: '👤',
                      color: 'text-pink-600 dark:text-pink-400',
                      title: 'Profile',
                      desc: 'Access your posts, update your image, track likes, coin balance, and view redemption history.',
                    },
                    {
                      icon: '⚙️',
                      color: 'text-yellow-600 dark:text-yellow-400',
                      title: 'Settings',
                      desc: 'Toggle dark mode, check the privacy policy or terms, and log out when needed.',
                    },
                    {
                      icon: '💰',
                      color: 'text-emerald-600 dark:text-emerald-400',
                      title: 'Earn Coins',
                      desc: 'Every like on your post turns into a coin. Redeem them for real rewards from your dashboard!',
                    },
                  ].map(({ icon, color, title, desc }) => (
                    <li key={title} className="flex items-start gap-4">
                      <span className={`${color} text-2xl flex-shrink-0`}>{icon}</span>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-gray-100">{title}</p>
                        <p className="text-gray-700 dark:text-gray-300">{desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </div>

          <Routes>
            <Route path="/user-profile/:userId" element={<UserProfile />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/admin"
              element={
                <AdminRoute user={user}>
                  <AdminPanel />
                </AdminRoute>
              }
            />
            <Route path="/terms" element={<Terms />} />
            <Route path="/about" element={<About />} />
            <Route path="/*" element={<Pins user={user} />} />
          </Routes>
          <Footer />
        </main>
      </div>
    </div>
  );
};

export default Home;
